import {Component, DoCheck, ElementRef, EventEmitter, Input, Output, QueryList, Renderer2, Self, SimpleChanges, ViewChildren} from '@angular/core';
import {ControlValueAccessor, NgControl} from '@angular/forms';
import {CheckboxOption} from './checkbox-option';

let CHECKBOX_ID = 0;

@Component({
  selector: 'valg-checkbox-group',
  templateUrl: './checkbox-group.component.html'
})
export class CheckboxGroupComponent implements ControlValueAccessor, DoCheck{
  public id = `valg-checkbox-in-group-${CHECKBOX_ID++}`;
  @Output() changeEvent = new EventEmitter<void>();

  set(value: any[]) {
    this.checkedList = value;
    this.onChange(this.checkedList);
  }

  @Input()
  public larger: boolean = false; 

  @Input()
  public get required(): boolean {
    return this._required;
  }
  public set required(value: boolean) {
    this._required = (value as any) === '' || value;
  }

  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(boolean: boolean) {
    this._disabled = boolean;
  }

  @Input() public collection!: CheckboxOption[];

  @Input() errorKey: string | undefined;

  @Input() canSelectAll: boolean = false;

  @Input() selectAllTitle = 'standard_input.option_input_select_all';

  @Input() direction : 'row' | 'column' = 'column';
  
  @Input() preSelectAll : boolean = false;

  @Input() twoColumnsOptionsDefaultLength: number = 5;

  @ViewChildren('checkbox')
  public checkboxCollection!: QueryList<ElementRef>;

  public collectionColumns: Array<CheckboxOption[]> = [];

  private _currentCollection!: CheckboxOption[];


  constructor(
    @Self() public control: NgControl,
    protected renderer:Renderer2) {
    control.valueAccessor = this;
  }

  ngDoCheck(): void {
    if (this._currentCollection?.length != this.collection?.length) {
      this._currentCollection = [...this.collection];
      this.splitCollectionInColumns();
    }
  }

  ngOnChanges(changes: SimpleChanges): void{
    if(changes['collection']){
      this.splitCollectionInColumns();
    }
  }

  get formControl() {
    return this.control;
  }

  public onChange: any = () => {}
  public onTouched: any = () => {}
  
  private _disabled!: boolean;
  private _required!: boolean;

  public checkedList: any[] = [];

  public checkboxSelect(value: any){
    if (this.preSelectAll) {
      return true
    } else {
      return this.contains(value);
    }
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  public writeValue(value: any) {
    this.checkedList = value !== null ? value : [];
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.checkDisabledState();
  }

  private checkDisabledState() {
    if (this.checkboxCollection && this.checkboxCollection.length > 0) {
      this.checkboxCollection.forEach((el) => {
        this.disabled
          ? this.renderer.setAttribute(el.nativeElement, 'disabled', String(this.disabled))
          : this.renderer.removeAttribute(el.nativeElement, 'disabled');
      });
    }
  }

  private splitCollectionInColumns() : void{
    let collectionColumns: Array<CheckboxOption[]>= [];
    if(this.collection?.length > this.twoColumnsOptionsDefaultLength){
      const half = Math.ceil(this.collection.length / 2);
      collectionColumns.push(this.collection.slice(0, half));
      if (this.collection?.length % 2 === 0) {
        collectionColumns.push(this.collection.slice(-half));
      } else {
        collectionColumns.push(this.collection.slice(-half+1));
      }
    } else{
      collectionColumns.push(this.collection)
    }
    this.collectionColumns = collectionColumns;
  }

  toggleCheck(value: any) {
    if (this.contains(value)) {
      this.remove(value);
    } else {
      this.add(value);
    }
    this.changeEvent.emit();
  }

  toggleSelectAll(){
    let checkAll = false;
    if(!this.isAllChecked()){
      checkAll = true;
    }
    this.collection.forEach(item => {
      if(checkAll && !item.disabled){
        this.add(item.value);
      } 
      else if(!item.disabled){
        this.remove(item.value);
      }
    });
    this.changeEvent.emit();
  }

  isAllChecked(){
    var collectionWithNoDisabledItems = this.collection.filter(x => !x.disabled).map(x => x.value);
    var checkedListWithNoDisabledItems = this.checkedList.filter(x => collectionWithNoDisabledItems.includes(x))
    return collectionWithNoDisabledItems.length === checkedListWithNoDisabledItems.length
  }

  contains(value: any): boolean {
    return this.checkedList.indexOf(value) > -1;
  }

  private add(value: any) {
    if (!this.contains(value)) {
      this.checkedList.push(value);
      this.onChange(this.checkedList);
    }
  }

  private remove(value: any) {
    const index = this.checkedList.indexOf(value);
    if (!this.checkedList || index < 0) {
      return;
    }

    this.checkedList.splice(index, 1);
    this.onChange(this.checkedList);
  }

  public onBlur(): void {
    this.onTouched();
  }
}